import { useEffect } from 'react'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import './App.css'
import DisclaimerPrivacyPolicy, {
  PrivacyPolicy,
  TermsAndConditions,
} from './Components/Disclaimer/Disclaimer'

const marketer = {
  name: 'Shashi Kumar Gupta',
  phone: '+919663606491',
  displayPhone: '+91 96636 06491',
  agentRera: 'PRM/KA/RERA/1251/308/AG/250811/005905',
}

const brandName = 'KeytoAbode'

const projects = [
  {
    name: 'Villa Bellissimo',
    type: 'Modern row villas',
    location: 'Hulimangala, near Electronic City',
    image: '/assets/banner-image.webp',
    badge: '4 BHK villas',
    price: 'On request',
    rera: 'PRM/KA/RERA/1251/446/PR/040823/006113',
    url: 'https://urbanizevillabellissimo.com/',
    summary:
      'A low-density villa community by Urbanize Group with 93 row villas across 8 acres and a 20,000 sq.ft. clubhouse.',
    facts: ['East, West and North villa options', 'Back alleys for better privacy', 'Home automation and premium finishes'],
  },
  {
    name: 'Indian Springs',
    type: 'Residential plotted community',
    location: 'Vabasandra, Hulimangala',
    image: '/assets/life-at-indian-spring.jpeg',
    badge: '1500-1920 sq.ft. plots',
    price: 'Rs. 1.41 Cr onwards',
    rera: 'PRM/KA/RERA/1251/308/PR/100125/007380',
    url: 'https://urbanizeindiansprings.com/',
    summary:
      'RERA registered plotted development with 207 units, green planning, clubhouse recreation, and possession listed for Dec 2026.',
    facts: ['Near Treamis School', 'Metro access around Bommasandra and Hebbagodi', 'Rainwater harvesting and STP'],
  },
  {
    name: 'Sterlitee Regal Park',
    type: 'Private villa enclave',
    location: 'Neotown, Electronic City',
    image: '/assets/gallery3.jpg',
    badge: '4 and 5 BHK villas',
    price: 'Check price',
    rera: 'PRM/KA/RERA/1251/308/PR/180925/008098',
    url: 'https://sterliteeregalpark.co.in/',
    summary:
      'A master-planned villa address positioned around open sky living, parks, private architecture, and club amenities.',
    facts: ['251 exclusive villas', '70% open-to-sky planning', '10 lush green parks'],
  },
  {
    name: 'Ayana Woods',
    type: 'Balinese-inspired estates',
    location: 'Sarjapura, Bengaluru',
    image: '/assets/amenities-4.jpg',
    badge: '4 and 5 BHK villas',
    price: 'Private consultation',
    rera: 'RERA approved, number to verify',
    url: 'https://ayana-woods.com/',
    summary:
      'A tropical estate villa concept with indoor-outdoor layouts, resort amenities, and villa sizes up to 6,400 sq.ft. BUA.',
    facts: ['75-acre tropical estate positioning', '4-acre resort club', '100+ curated amenities'],
  },
]

const highlights = [
  ['Curated portfolio', 'Four villa and plotted communities across Electronic City, Hulimangala, Neotown, and Sarjapura.'],
  ['RERA-aware guidance', 'Agent and project registration details are visible before you start a site visit conversation.'],
  ['Direct marketer support', 'Speak with Shashi Kumar Gupta for pricing, floor plans, location fit, and visit coordination.'],
]

const howItWorksSteps = [
  'Buyers explore project information on KeytoAbode or our project websites.',
  'Buyers submit inquiries for projects of interest.',
  "KeytoAbode shares inquiries with the builder's authorized sales team.",
  'All site visits, pricing discussions, bookings, and transactions are handled directly by the developer.',
]

const steps = [
  'Shortlist by budget, plot/villa preference, and school or IT corridor access.',
  'Compare possession, community scale, amenities, and RERA details.',
  'Book a focused site visit with only the projects that match your buying intent.',
]

function useRevealAnimation() {
  useEffect(() => {
    const nodes = document.querySelectorAll('[data-reveal]')
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15 },
    )

    nodes.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [])
}

function whatsappLink(projectName = `${brandName} projects`) {
  const text = `Hello Shashi Kumar Gupta, I am interested in ${projectName}. Please share details and help me plan a site visit.`
  return `https://api.whatsapp.com/send?phone=${marketer.phone.replace('+', '')}&text=${encodeURIComponent(text)}`
}

function projectRedirectUrl(projectName) {
  return projects.find((project) => project.name === projectName)?.url || '#projects'
}

function websiteLabel(url) {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
}

function Logo() {
  return (
    <a className="brand" href="#home" aria-label={`${brandName} home`}>
      <img src="/keytoabode-logo.svg" alt="" className="brand-mark" />
      <span>
        <strong>{brandName}</strong>
        <small>Curated Bengaluru homes</small>
      </span>
    </a>
  )
}

function Header() {
  return (
    <header className="site-header">
      <Logo />
      <nav aria-label="Primary navigation">
        <a href="#projects">Projects</a>
        <a href="#compare">Compare</a>
        <a href="#rera">RERA</a>
        <a href="#about">About Us</a>
        <a href="#how-it-works">How It Works</a>
        <a href="#contact">Contact</a>
      </nav>
      <a className="header-cta" href={whatsappLink()} target="_blank" rel="noreferrer">
        Talk to Shashi Kumar Gupta
      </a>
    </header>
  )
}

function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-copy" data-reveal>
        <p className="eyebrow">Authorised real estate marketing partner</p>
        <h1>KeytoAbode</h1>
        <p className="hero-text">
          Clean, practical shortlisting for premium plotted developments and villas in South and East Bengaluru.
        </p>
        <div className="hero-actions">
          <a className="primary-btn" href="#projects">View Projects</a>
          <a className="secondary-btn" href={whatsappLink()} target="_blank" rel="noreferrer">Book Site Visit</a>
        </div>
      </div>

      <form
        className="lead-panel"
        data-reveal
        onSubmit={(event) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          const project = form.get('project') || `${brandName} projects`
          window.open(projectRedirectUrl(project), '_blank', 'noopener,noreferrer')
        }}
      >
        <span className="panel-label">Speak with</span>
        <h2>{marketer.name}</h2>
        <label>
          Name
          <input name="name" type="text" placeholder="Your name" required />
        </label>
        <label>
          Phone
          <input name="phone" type="tel" placeholder="Mobile number" required />
        </label>
        <label>
          Interested in
          <select name="project" defaultValue="Indian Springs">
            {projects.map((project) => (
              <option value={project.name} key={project.name}>{project.name}</option>
            ))}
          </select>
        </label>
        <button type="submit">Request Details</button>
      </form>
    </section>
  )
}

function Highlights() {
  return (
    <section className="section split-section" data-reveal>
      <div>
        <p className="eyebrow">Simple buying support</p>
        <h2>One contact for four serious options.</h2>
      </div>
      <div className="highlight-grid">
        {highlights.map(([title, text]) => (
          <article className="mini-card" key={title}>
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </section>
  )
}

function ProjectCard({ project, index }) {
  return (
    <article className="project-card" data-reveal style={{ '--delay': `${index * 90}ms` }}>
      <a className="project-image" href={project.url} target="_blank" rel="noreferrer">
        <img src={project.image} alt={`${project.name} project visual`} loading="lazy" />
        <span>{project.badge}</span>
      </a>
      <div className="project-body">
        <p className="project-type">{project.type}</p>
        <h3>{project.name}</h3>
        <p className="project-location">{project.location}</p>
        <p>{project.summary}</p>
        <ul>
          {project.facts.map((fact) => (
            <li key={fact}>{fact}</li>
          ))}
        </ul>
        <div className="project-rera">
          <span>Project RERA</span>
          <strong>{project.rera}</strong>
        </div>
        <div className="project-footer">
          <strong>{project.price}</strong>
          <div className="project-actions">
            <a className="project-link" href={project.url} target="_blank" rel="noreferrer">
              {websiteLabel(project.url)}
            </a>
            <a href={project.url} target="_blank" rel="noreferrer">Enquire Now</a>
          </div>
        </div>
      </div>
    </article>
  )
}

function Projects() {
  return (
    <section className="section" id="projects">
      <div className="section-heading" data-reveal>
        <p className="eyebrow">Promoted projects</p>
        <h2>Premium choices with different buying profiles.</h2>
      </div>
      <div className="project-grid">
        {projects.map((project, index) => (
          <ProjectCard project={project} index={index} key={project.name} />
        ))}
      </div>
    </section>
  )
}

function Compare() {
  return (
    <section className="section compare-section" id="compare" data-reveal>
      <div className="section-heading">
        <p className="eyebrow">How to decide</p>
        <h2>Keep the shortlist practical.</h2>
      </div>
      <div className="compare-layout">
        <div className="steps">
          {steps.map((step, index) => (
            <div className="step" key={step}>
              <span>{index + 1}</span>
              <p>{step}</p>
            </div>
          ))}
        </div>
        <div className="compare-table" role="table" aria-label="Project comparison">
          <div role="row" className="table-head">
            <span>Project</span>
            <span>Best for</span>
            <span>Format</span>
          </div>
          {projects.map((project) => (
            <div role="row" key={project.name}>
              <span>{project.name}</span>
              <span>{project.name === 'Indian Springs' ? 'Plot buyers' : 'Villa buyers'}</span>
              <span>{project.badge}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Rera() {
  return (
    <section className="section rera-section" id="rera" data-reveal>
      <div>
        <p className="eyebrow">Transparency</p>
        <h2>{marketer.name}</h2>
        <p>
          Marketing partner for curated Bengaluru residential projects. Verify all project details, pricing,
          availability, payment terms, and RERA records before purchase.
        </p>
        <p>
          For queries and enquiries, contact Shashi Kumar Gupta.
        </p>
      </div>
      <div className="rera-box">
        <span>Agent RERA</span>
        <strong>{marketer.agentRera}</strong>
      </div>
    </section>
  )
}

function About() {
  return (
    <section className="section about-section" id="about" data-reveal>
      <div className="section-heading">
        <p className="eyebrow">About {brandName}</p>
        <h2>Independent real estate marketing support for Bengaluru homebuyers.</h2>
      </div>
      <div className="about-layout">
        <article className="about-card">
          <p>
            {brandName} is an independent real estate marketing platform based in Bengaluru. We help homebuyers
            discover residential projects and connect with authorized builder sales teams.
          </p>
          <p>
            We work with reputed developers and promote projects through dedicated project websites and digital
            marketing campaigns.
          </p>
        </article>
        <article className="about-card about-note">
          <p>
            {brandName} is a marketing and channel partner and is not the developer of the projects displayed on this
            website.
          </p>
          <p>
            {brandName} does not collect booking amounts, process property transactions, or make decisions on behalf of
            developers.
          </p>
        </article>
      </div>
      <div className="about-projects">
        <h3>Project Websites Managed by {brandName}</h3>
        <div className="about-project-links">
          {projects.map((project) => (
            <a key={project.name} href={project.url} target="_blank" rel="noreferrer">
              {project.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  return (
    <section className="section how-it-works-section" id="how-it-works" data-reveal>
      <div className="section-heading">
        <p className="eyebrow">How It Works</p>
        <h2>How KeytoAbode works from first view to developer handoff.</h2>
      </div>
      <div className="how-it-works-grid">
        {howItWorksSteps.map((step, index) => (
          <article className="how-it-works-card" key={step}>
            <span>{index + 1}</span>
            <p>{step}</p>
          </article>
        ))}
      </div>
    </section>
  )
}

function Contact() {
  return (
    <section className="contact-section" id="contact" data-reveal>
      <div>
        <p className="eyebrow">Next step</p>
        <h2>Plan a site visit with context, not pressure.</h2>
        <p>
          Share your preferred budget, home type, and timeline. Shashi Kumar Gupta will help you compare the
          relevant projects and schedule visits.
        </p>
        <p>
          For queries and enquiries, contact Shashi Kumar Gupta directly.
        </p>
        <p>
          Address: GR Gardenia, Maragondanahalli, Karnataka 560105
        </p>
        <p>
          Email: <a href="mailto:shashigupta@keytoabode.com">shashigupta@keytoabode.com</a>
        </p>
      </div>
      <div className="contact-actions">
        <a className="primary-btn" href={whatsappLink()} target="_blank" rel="noreferrer">WhatsApp Now</a>
        <a className="secondary-btn" href={`tel:${marketer.phone}`}>Call {marketer.displayPhone}</a>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <Logo />
      <p>
        {brandName} is an independent real estate marketing platform. We act as marketing partners for developers and
        assist prospective buyers by providing project information and connecting them with authorized builder sales
        teams. For queries and enquiries, contact Shashi Kumar Gupta.
      </p>
      <p>
        We do not represent developers directly and do not process property transactions.
      </p>
      <div className="footer-links">
        <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        <a href="/terms-and-conditions/" target="_blank" rel="noopener noreferrer">Terms</a>
        <a href="/disclaimer" target="_blank" rel="noopener noreferrer">Disclaimer</a>
      </div>
    </footer>
  )
}

function HomePage() {
  useRevealAnimation()

  return (
    <>
      <Header />
      <main>
        <Hero />
        <About />
        <HowItWorks />
        <Highlights />
        <Projects />
        <Compare />
        <Rera />
        <Contact />
      </main>
      <a className="floating-whatsapp" href={whatsappLink()} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp">
        <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
          <path d="M16 3.2C8.94 3.2 3.2 8.82 3.2 15.74c0 2.42.72 4.78 2.08 6.82L3.92 28.8l6.4-1.34A12.9 12.9 0 0 0 16 28.28c7.06 0 12.8-5.62 12.8-12.54S23.06 3.2 16 3.2Zm0 22.92c-1.78 0-3.52-.45-5.06-1.3l-.36-.2-3.8.8.8-3.68-.24-.38a10.13 10.13 0 0 1-1.96-5.62C5.38 10 10.14 5.36 16 5.36S26.62 10 26.62 15.74 21.86 26.12 16 26.12Zm5.82-7.76c-.32-.16-1.88-.92-2.18-1.02-.3-.12-.52-.16-.74.16-.22.32-.84 1.02-1.04 1.24-.18.22-.38.24-.7.08-.32-.16-1.36-.5-2.6-1.58-.96-.84-1.6-1.88-1.8-2.2-.18-.32-.02-.5.14-.66.14-.14.32-.38.48-.56.16-.2.22-.32.32-.54.12-.22.06-.4-.02-.56-.08-.16-.74-1.76-1.02-2.42-.26-.64-.54-.54-.74-.56h-.62c-.22 0-.56.08-.86.4-.3.32-1.12 1.08-1.12 2.64s1.14 3.06 1.3 3.28c.16.22 2.24 3.38 5.44 4.74.76.32 1.36.5 1.82.64.76.24 1.46.2 2 .12.62-.1 1.88-.76 2.14-1.5.26-.74.26-1.38.18-1.5-.08-.14-.28-.22-.6-.38Z" />
        </svg>
      </a>
      <Footer />
    </>
  )
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/disclaimer" element={<DisclaimerPrivacyPolicy />} />
        <Route path="/privacy-policy/" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions/" element={<TermsAndConditions />} />
      </Routes>
    </Router>
  )
}

export default App
